import { useMutation } from '@tanstack/react-query';
import { Dropdown, DropdownAddOption, DropdownOption } from '@volvo/vcdk-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'underscore';

import type { ILocation } from '@/service/vpc/types/IUnitRegistration';
import type { DropdownSize } from '@volvo/vcdk';

import { vpcService } from '@/service/vpc/vpcService';

type Props = {
  label: string;
  defaultValue: string;
  disabled?: boolean;
  countrycode?: string;
  size?: DropdownSize | undefined;
  helperText?: string;
  setLocation: (location: ILocation) => void;
};

type DropdownOptionItem = {
  text: string;
  value: string;
};

const GeoComboField: React.FC<Props> = ({
  defaultValue = '',
  label,
  setLocation,
  disabled,
  countrycode,
  helperText,
  size = 'medium',
}) => {
  const dropdownRef = useRef<Dropdown.Element<false>>(null);
  const [value, setValue] = useState<string>(defaultValue);
  const [search, setSearch] = useState<string>('');
  const [options, setOptions] = useState<DropdownOptionItem[]>(() => {
    // Initialize with defaultValue if it exists
    if (defaultValue) {
      return [{ text: defaultValue, value: defaultValue }];
    }
    return [];
  });

  const { mutateAsync: fetchAutocomplete } = useMutation({
    mutationFn: async ({ input, countrycode }: { input: string; countrycode: string }) =>
      vpcService.Geocoding.autocomplete({ input, countrycode }),
    onMutate() {
      setOptions([]);
    },
    onSuccess(data) {
      const newOptions = data.results.map((item) => ({
        text: item.formatted,
        value: item.place_id,
      }));
      setOptions(newOptions);
    },
  });

  const { mutateAsync: fetchGeocode } = useMutation({
    mutationFn: async (placeId: string) => vpcService.Geocoding.placeById({ placeId }),
  });

  const debouncedFetchAutocomplete = useMemo(
    () =>
      debounce((input: string, countrycode: string) => {
        fetchAutocomplete({ input, countrycode });
      }, 500),
    [fetchAutocomplete]
  );

  const handleSelectOption = async (placeId: string) => {
    setValue(placeId);
    if (placeId === 'custom_value') {
      return;
    }
    try {
      const geocodeData = await fetchGeocode(placeId);
      const result = geocodeData.features[0];
      if (result) {
        const location: ILocation = {
          street: result.properties.address_line1 || '',
          city: result.properties.city || '',
          postal: result.properties.postcode || '',
          country: result.properties.country || '',
          homePort: result.properties.formatted || '',
          latitude: result.properties.lat || 0,
          longitude: result.properties.lon || 0,
        };
        setLocation(location);
      }
    } catch (error) {
      console.error('Failed to fetch geocode details:', error);
    }
  };

  const handleAddOption = () => {
    setOptions((prevOptions) => [...prevOptions, { text: search, value: 'custom_value' }]);
    setValue(search);
    setLocation({
      street: '',
      city: '',
      postal: '',
      country: '',
      homePort: search,
      latitude: 0,
      longitude: 0,
    });
  };

  useEffect(() => {
    if (search.length <= 2) {
      return;
    }
    debouncedFetchAutocomplete(search, 'se');
  }, [search, debouncedFetchAutocomplete, countrycode]);

  // When options are populated/updated, tell the popover to recalculate its
  // position/size so it expands to fit all new options instead of keeping
  // stale dimensions with a scroll bar.
  useEffect(() => {
    const popover = dropdownRef.current?.shadowRoot?.querySelector('vcdk-dropdown-popover') as
      | (HTMLElement & { updatePosition(): void })
      | null;
    popover?.updatePosition();
  }, [options]);

  // const optionsFiltered = useCallback(() => {
  //   return options;
  // }, [options]);

  return (
    <div className="relative">
      <Dropdown
        ref={dropdownRef}
        id="GeoComboField"
        multi={false}
        size={size}
        filterInput
        placement="bottom-start"
        floatingLabel={false}
        label={label}
        disabled={disabled}
        value={value}
        helperText={helperText}
        className="[&::part(input-wrapper)]:h-10"
        onChange={(e) => {
          const placeId = e.detail.value;
          if (placeId) {
            handleSelectOption(placeId);
          }
        }}
        onInput={(e) => {
          setSearch(e.detail.value);
          if (!e.detail.value) {
            setValue('');
            setOptions([]);
          }
        }}
      >
        {options.map((option, idx) => (
          <DropdownOption key={`${option.value}-${idx}`} value={option.value} text={option.text}>
            {option.text}
          </DropdownOption>
        ))}

        {search && options.every((option) => option.text !== search) && (
          <DropdownAddOption onClick={handleAddOption}>
            <span>Add option</span>
            <span>&nbsp;</span>
            <span> "{search}" </span>
          </DropdownAddOption>
        )}
      </Dropdown>
    </div>
  );
};

export default GeoComboField;
